import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { api, errMsg, buildAudioUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { PageHeader, Disclaimer } from "@/components/Common";
import { ReasonModal } from "@/components/ReasonModal";
import { PriorityBadge, PriorityTierControl } from "@/components/PriorityTierControl";
import { getLifecycleStatus, isTrackBlockedForNewWork, lifecycleBadgeVariant } from "@/lib/withdrawGate";
import { WITHDRAW_REASONS, ARCHIVE_REASONS } from "@/lib/lists";
import StatusBadge, { DocStatusBadge } from "@/components/StatusBadge";
import { VaultDocumentsPanel } from "@/components/VaultDocumentsPanel";
import { CLEARANCE_STATUSES, READY_STATUSES, DOC_TYPES, DOC_STATUSES, EXPLICIT_CLEAN, RIGHTS_STATUSES, MASTER_STATUS_OPTIONS, PUBLISHING_STATUS_OPTIONS, SYNC_RIGHTS_STATUS_OPTIONS, MECHANICAL_STATUS_OPTIONS, MOOD_TAGS, SONGTRUST_STATUS_OPTIONS, SOUNDEXCHANGE_STATUS_OPTIONS, MLC_STATUS_OPTIONS, HFA_STATUS_OPTIONS, MECHANICAL_LICENSE_SCOPE_OPTIONS } from "@/lib/constants";
import { GENRES, SUB_GENRES, BEST_USE_OPTIONS, TERRITORY_OPTIONS, ATTORNEY_REVIEW_REASONS, OTHER } from "@/lib/lists";
import { SearchableSelect, OtherField } from "@/components/Controlled";
import { useAuth, canChangeClearance, canWrite, canFlagAttorneyReview, isAdmin } from "@/lib/auth";
import { formatDurationMMSS, parseDurationInput } from "@/lib/duration";
import { formatNotes, hasVisibleNotes } from "@/lib/safeDisplay";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";
import { DeleteRecordButton } from "@/components/DeleteRecordButton";
import { ArrowLeft, Save, Upload, ShieldAlert, AlertTriangle, CheckCircle2, Trash2, Plus, Gavel, X, Sparkles, FolderOpen, Send, History, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PublishToGFMPPanel from "@/components/PublishToGFMP";
import AIProvenancePanel from "@/components/AIProvenancePanel";
import { RightsPartySection } from "@/components/RightsParty";
import { AssetPathwayBadge } from "@/components/AssetPathwayBadge";
import { AssetPathwayDialog } from "@/components/AssetPathwayDialog";

const BLANK = {
  track_title: "", catalog_code: "", catalog_name: "", artist: "", featured_artist: "",
  writers: [], publishers: [], publisher_admin: "", master_owner: "", label_or_imprint: "",
  // MP11 Phase 1 — structured rights parties. Backend synthesises these from
  // the legacy name-string arrays on read for existing tracks, so this is a
  // safe default for both new and edited tracks.
  writer_parties: [],
  publisher_parties: [],
  administrator_parties: [],
  rightsholder_parties: [],
  genre: "", sub_genre: "", mood_tags: [], bpm: null, duration: null,
  explicit_clean_status: "", best_use: "",
  // Companion free-text for "Other" picks. Stored on the track record so
  // round-trips survive without backend schema churn (TrackUpsert allows extras).
  genre_other: "", sub_genre_other: "", best_use_other: "", territory_other: "",
  lyrics_available: false, instrumental_available: false, stems_available: false, alt_versions_available: false,
  isrc: "", iswc: "", ipi_cae: "", upc: "", release_date: "", copyright_year: "",
  loc_registration_number: "", dacr_certificate_number: "",
  master_status: "", publishing_status: "", sync_rights_status: "", mechanical_status: "",
  pro_affiliation: "", mlc_status: "", soundexchange_status: "", songtrust_or_admin_status: "",
  hfa_status: "", mechanical_license_scope: "",
  territory_restrictions: "", sample_or_interpolation_flag: false, sample_notes: "",
  ownership_dispute_flag: false, estate_or_heirship_flag: false, third_party_approval_required: false,
  next_action: "", internal_notes: "",
};

const CSV = (v) => Array.isArray(v) ? v.join(", ") : (v || "");
const toArr = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean);

export default function TrackEditor({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(BLANK);
  const [saved, setSaved] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(searchParams.get("tab") || "core");
  const [clearanceOpen, setClearanceOpen] = useState(false);
  const [clearanceTarget, setClearanceTarget] = useState("");
  const [clearanceNotes, setClearanceNotes] = useState("");
  const [attorneyOpen, setAttorneyOpen] = useState(false);
  const [attorneyReasonCategory, setAttorneyReasonCategory] = useState("");
  const [attorneyReasonDetail, setAttorneyReasonDetail] = useState("");
  const [pathwayOpen, setPathwayOpen] = useState(false);
  const audioInputRef = useRef(null);

  async function refresh(tid) {
    const r = await api.get(`/tracks/${tid}`);
    setSaved(r.data);
    setData({
      ...BLANK,
      ...r.data,
      writers: r.data.writers || [],
      publishers: r.data.publishers || [],
      mood_tags: r.data.mood_tags || [],
      // MP11 — bring in the structured party arrays (backend synthesises
      // them from legacy name-string fields when the DB has none yet).
      writer_parties:        r.data.writer_parties || [],
      publisher_parties:     r.data.publisher_parties || [],
      administrator_parties: r.data.administrator_parties || [],
      rightsholder_parties:  r.data.rightsholder_parties || [],
    });
    const c = await api.get(`/tracks/${tid}/compliance`);
    setEvaluation(c.data.evaluation);
    setDocuments(c.data.documents || []);
  }

  useEffect(() => {
    if (mode === "edit" && id) {
      refresh(id).catch(e => toast.error(errMsg(e))).finally(()=>setLoading(false));
    }
  }, [mode, id]);

  function setField(k, v) { setData(d => ({ ...d, [k]: v })); }

  async function save() {
    // MP4 — client-side guard: a Catalog Code is required for new tracks.
    // The server enforces this as the source of truth; this guard simply
    // prevents an obvious round-trip on submit.
    if (mode === "new" && !(data.catalog_code || "").trim()) {
      toast.error("Please select a Catalog Code before saving.");
      return;
    }
    setSaving(true);
    try {
      const parsedDuration = (data.duration === "" || data.duration == null)
        ? null
        : (typeof data.duration === "number" ? data.duration : parseDurationInput(data.duration));
      if (data.duration !== "" && data.duration != null && parsedDuration == null) {
        setSaving(false);
        toast.error("Invalid duration. Use MM:SS (e.g. 3:42) or raw seconds (e.g. 222).");
        return;
      }
      // MP11 Phase 1 — if the operator has been working with structured
      // parties, prefer those as the source of truth and derive the legacy
      // name-string arrays on the client too. The server does the same, but
      // this keeps the request body internally consistent.
      const derivedWriters = (data.writer_parties || [])
        .map((p) => (p?.name || "").trim())
        .filter(Boolean);
      const derivedPublishers = (data.publisher_parties || [])
        .map((p) => (p?.name || "").trim())
        .filter(Boolean);
      const derivedAdmins = (data.administrator_parties || [])
        .map((p) => (p?.name || "").trim())
        .filter(Boolean);
      const payload = { ...data,
        writers: derivedWriters.length ? derivedWriters : (Array.isArray(data.writers) ? data.writers : toArr(data.writers)),
        publishers: derivedPublishers.length ? derivedPublishers : (Array.isArray(data.publishers) ? data.publishers : toArr(data.publishers)),
        publisher_admin: derivedAdmins.length ? derivedAdmins.join("; ") : (data.publisher_admin || ""),
        mood_tags: Array.isArray(data.mood_tags) ? data.mood_tags : toArr(data.mood_tags),
        bpm: data.bpm === "" || data.bpm == null ? null : parseInt(data.bpm, 10),
        duration: parsedDuration,
      };
      let tid = id;
      if (mode === "new") {
        const r = await api.post("/tracks", payload);
        tid = r.data.id;
        toast.success("Track created");
        navigate(`/tracks/${tid}`, { replace: true });
      } else {
        await api.put(`/tracks/${id}`, payload);
        toast.success("Track saved");
        await refresh(id);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  }

  async function uploadAudio(e) {
    const file = e.target.files?.[0]; if (!file) return;
    if (!id) { toast.error("Save the track before uploading audio"); return; }
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/tracks/${id}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio uploaded"); await refresh(id);
    } catch (err) { toast.error(errMsg(err)); }
    finally { if (audioInputRef.current) audioInputRef.current.value = ""; }
  }

  async function setClearance() {
    try {
      await api.post(`/tracks/${id}/clearance`, { clearance_status: clearanceTarget, notes: clearanceNotes });
      toast.success(`Clearance set to ${clearanceTarget}`);
      setClearanceOpen(false); setClearanceNotes(""); setClearanceTarget("");
      await refresh(id);
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function flagAttorney(required) {
    try {
      let reasonStr = "";
      if (required) {
        if (attorneyReasonCategory && attorneyReasonCategory !== OTHER) {
          reasonStr = attorneyReasonDetail.trim()
            ? `${attorneyReasonCategory} — ${attorneyReasonDetail.trim()}`
            : attorneyReasonCategory;
        } else if (attorneyReasonCategory === OTHER) {
          reasonStr = attorneyReasonDetail.trim() || OTHER;
        }
      }
      await api.post(`/tracks/${id}/attorney-review`, {
        attorney_review_required: required,
        attorney_review_reason: reasonStr,
      });
      toast.success(required ? "Flagged for Attorney Review" : "Attorney Review cleared");
      setAttorneyOpen(false); setAttorneyReasonCategory(""); setAttorneyReasonDetail("");
      await refresh(id);
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function addDocument(docType) {
    try {
      await api.post("/documents", {
        linked_track_id: id, document_type: docType, document_status: "Missing",
      });
      toast.success(`Added ${docType}`); await refresh(id);
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function updateDocStatus(docId, newStatus) {
    const d = documents.find(x => x.id === docId);
    try {
      await api.put(`/documents/${docId}`, { ...d, document_status: newStatus });
      toast.success("Document status updated"); await refresh(id);
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function deleteDocument(docId) {
    if (!window.confirm("Delete this document record?")) return;
    try { await api.delete(`/documents/${docId}`); toast.success("Deleted"); await refresh(id); }
    catch (e) { toast.error(errMsg(e)); }
  }

  // Phase A Run 2 — Lifecycle transition (Withdraw / Archive / Reactivate).
  // The backend endpoint `POST /api/tracks/{id}/lifecycle` requires a
  // ReasonedActionReq (category + detail, min 10 chars). We show the
  // shared ReasonModal to capture both fields before sending.
  const [lifecycleModal, setLifecycleModal] = useState(null); // "withdraw" | "archive" | null
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  async function submitLifecycle({ target, actionLabel, category, detail }) {
    setLifecycleBusy(true);
    try {
      await api.post(`/tracks/${id}/lifecycle`, {
        target_status: target,
        category,
        detail,
      });
      toast.success(`${actionLabel} recorded and audited.`);
      setLifecycleModal(null);
      await refresh(id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLifecycleBusy(false);
    }
  }

  async function reactivateTrack() {
    if (!window.confirm("Reactivate this track? It will be eligible for new active workflows again.")) return;
    try {
      await api.post(`/tracks/${id}/lifecycle`, {
        target_status: "ACTIVE",
        category: "Compliance Review Outcome",
        detail: "Track reactivated by authorised operator. Prior audit history preserved.",
      });
      toast.success("Track reactivated");
      await refresh(id);
    } catch (e) { toast.error(errMsg(e)); }
  }

  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  const audioReady = !!saved?.audio_storage_path;
  const status = saved?.clearance_status;
  const showClearanceCTA = canChangeClearance(user) && mode === "edit";
  const writeAccess = canWrite(user);
  const showAttorneyCTA = canFlagAttorneyReview(user) && mode === "edit";
  // Phase A Run 2 — lifecycle controls
  const lifecycleStatus = getLifecycleStatus(saved);
  const isBlocked = isTrackBlockedForNewWork(saved);
  const lifecycleBadge = lifecycleBadgeVariant(lifecycleStatus);
  const isSuperAdmin = isAdmin(user);

  return (
    <div data-testid="track-editor-page">
      <PageHeader
        title={mode === "new" ? "New Track" : (saved?.track_title || "Track")}
        subtitle={mode === "new" ? "Enter core record — you can refine compliance after saving" : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs">{saved?.track_id}</span>
            <PriorityBadge tier={saved?.priority_tier} testid="track-priority-badge" />
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.14em] font-medium border ${lifecycleBadge.className}`}
              data-testid="track-lifecycle-badge"
            >
              {lifecycleBadge.label}
            </span>
            {isBlocked && (
              <span className="text-[11px] text-red-800/80" data-testid="track-lifecycle-blocked-note">
                Blocked from new sync / licensing / public workflows. Historical records preserved.
              </span>
            )}
          </div>
        )}
        actions={
          <>
            <Button variant="outline" onClick={()=>navigate("/tracks")}><ArrowLeft className="w-4 h-4 mr-2"/>Back</Button>
            {mode === "edit" && writeAccess && !isBlocked && (
              <>
                <Button variant="outline" size="sm" onClick={()=>setLifecycleModal("withdraw")} data-testid="track-withdraw-button" style={{borderColor:"hsl(0 70% 42%)", color:"hsl(0 70% 42%)"}}>
                  Withdraw
                </Button>
                <Button variant="outline" size="sm" onClick={()=>setLifecycleModal("archive")} data-testid="track-archive-button">
                  Archive
                </Button>
              </>
            )}
            {mode === "edit" && writeAccess && isBlocked && isSuperAdmin && (
              <Button variant="outline" size="sm" onClick={reactivateTrack} data-testid="track-reactivate-button">
                Reactivate
              </Button>
            )}
            {writeAccess ? (
              <Button onClick={save} disabled={saving} data-testid="track-save-button" style={{background:"hsl(347 72% 22%)"}}><Save className="w-4 h-4 mr-2"/>{saving ? "Saving…" : "Save"}</Button>
            ) : (
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="track-readonly-badge">Read-only access</span>
            )}
          </>
        }
      />

      {mode === "edit" && saved?.derived_from_demo_id && (
        <Card className="mb-4 border-amber-300/40" data-testid="track-derived-from-demo">
          <CardContent className="p-4 flex flex-wrap items-center gap-3 text-[12.5px]">
            <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.18em] font-medium"
              style={{ background: "hsl(40 48% 92%)", color: "hsl(347 60% 22%)", border: "1px solid hsl(40 48% 78%)" }}
            >
              New Master Derived From Demo
            </span>
            <span className="text-muted-foreground">
              Promoted from a public Kandake demo submission. Compliance gates were enforced before promotion.
            </span>
            <div className="ml-auto">
              <Button asChild size="sm" variant="outline" data-testid="track-derived-open-demo">
                <Link to={`/demos/${saved.derived_from_demo_id}`}>
                  Open source demo
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Material Processing Priority — human-approved workflow support only.
          Priority Tier does NOT bypass Asset Pathway, DEMO_ONLY, clearance, or
          lifecycle gating. Change is audited with reason + material=true. */}
      {mode === "edit" && (
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Material Processing Priority</div>
              <div className="text-[11.5px] text-muted-foreground/90 mt-0.5">
                Sort / filter signal only. Does not override Asset Pathway, clearance, or lifecycle.
              </div>
            </div>
            <PriorityTierControl
              record={saved}
              endpoint={`/tracks/${id}/priority`}
              onChanged={() => refresh(id).catch(() => {})}
              canEdit={writeAccess}
              compact
              testidPrefix="track-priority"
            />
          </CardContent>
        </Card>
      )}

      {/* MP10 — Industry Beta Access (super_admin only). Surfaced as a
          dedicated card so toggling here is a deliberate approval action,
          not an incidental side-effect of a generic Save. Backend audit-
          logs every change via PUT /tracks/{id}/beta-flags. */}
      {mode === "edit" && isAdmin?.(user) && (
        <BetaAccessCard
          trackId={id}
          betaVisible={!!saved?.beta_visible}
          industryReviewApproved={!!saved?.industry_review_approved}
          onSaved={() => refresh(id).catch(() => {})}
        />
      )}

      {mode === "edit" && (
        <Card className="mb-4" data-testid="track-asset-pathway-card">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Asset Pathway</span>
            <AssetPathwayBadge value={saved?.asset_pathway} />
            {saved?.asset_pathway_reason && (
              <span className="text-[11px] text-muted-foreground italic truncate max-w-[380px]" title={saved.asset_pathway_reason}>
                &ldquo;{saved.asset_pathway_reason}&rdquo;
              </span>
            )}
            <div className="ml-auto flex flex-wrap gap-2">
              {(isAdmin?.(user) || user?.role === "catalog_manager") && (
                <Button size="sm" variant="outline" onClick={() => setPathwayOpen(true)} data-testid="track-change-pathway">
                  Change pathway
                </Button>
              )}
              <Button asChild size="sm" variant="outline" data-testid="track-open-compliance">
                <Link to={`/tracks/${id}/compliance`}>
                  Open compliance workspace
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === "edit" && (
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <StatusBadge status={status} />
            <span className="text-xs text-muted-foreground">Risk: <span className="font-medium text-foreground">{saved?.risk_level || "—"}</span></span>
            {saved?.attorney_review_required && (
              <span className="text-xs flex items-center gap-1 text-[hsl(0_70%_42%)]"><Gavel className="w-3.5 h-3.5"/> Attorney Review Required</span>
            )}
            <div className="ml-auto flex flex-wrap gap-2">
              {showClearanceCTA && (
                <>
                  <Button variant="outline" onClick={()=>{setClearanceTarget("Internally Ready"); setClearanceOpen(true);}} data-testid="track-set-internally-ready" disabled={!evaluation?.eligible_internally_ready || isBlocked}>Internally Ready</Button>
                  <Button variant="outline" onClick={()=>{setClearanceTarget("Client-Ready"); setClearanceOpen(true);}} data-testid="track-set-client-ready" disabled={!evaluation?.eligible_client_ready || isBlocked}>Mark Client-Ready</Button>
                  <Button onClick={()=>{setClearanceTarget("Corporate-Ready"); setClearanceOpen(true);}} data-testid="track-set-corporate-ready" disabled={!evaluation?.eligible_corporate_ready || isBlocked} style={{background:"hsl(40 48% 40%)"}}>Mark Corporate-Ready</Button>
                </>
              )}
              {showAttorneyCTA && (
                <Button variant="outline" onClick={()=>setAttorneyOpen(true)} data-testid="track-attorney-review-button"><Gavel className="w-4 h-4 mr-2"/>Attorney Review</Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={(v)=>{setTab(v); setSearchParams(v === "core" ? {} : { tab: v });}}>
        <TabsList className="flex flex-wrap gap-1 bg-secondary">
          <TabsTrigger value="core" data-testid="tab-core">Core</TabsTrigger>
          <TabsTrigger value="identifiers" data-testid="tab-identifiers">Identifiers</TabsTrigger>
          <TabsTrigger value="rights" data-testid="tab-rights">Rights</TabsTrigger>
          {mode === "edit" && <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>}
          {mode === "edit" && <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>}
          {mode === "edit" && <TabsTrigger value="audio" data-testid="tab-audio">Audio</TabsTrigger>}
          {mode === "edit" && <TabsTrigger value="gfmp" data-testid="tab-gfmp-publish">GFMP Publish</TabsTrigger>}
          {mode === "edit" && <TabsTrigger value="ai-provenance" data-testid="tab-ai-provenance">AI Provenance</TabsTrigger>}
          {mode === "edit" && <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>}
        </TabsList>

        <TabsContent value="core">
          <Card><CardContent className="p-5 grid md:grid-cols-2 gap-4">
            <Field label="Track Title" required><Input value={data.track_title} onChange={e=>setField("track_title", e.target.value)} data-testid="track-title-input"/></Field>
            <Field label="Catalog Code" required={mode === "new"}>
              <CatalogCodePicker
                value={data.catalog_code}
                onChange={(v)=>setField("catalog_code", v)}
                onCatalogResolved={(cc) => {
                  // Auto-populate catalog_name from registry if currently empty
                  if (cc?.name && !(data.catalog_name || "").trim()) {
                    setField("catalog_name", cc.name);
                  }
                }}
                isNew={mode === "new"}
              />
            </Field>
            <Field label="Catalog Name"><Input value={data.catalog_name} onChange={e=>setField("catalog_name", e.target.value)} data-testid="track-catalog-name-input"/></Field>
            <Field label="Artist" required><Input value={data.artist} onChange={e=>setField("artist", e.target.value)} data-testid="track-artist-input"/></Field>
            <Field label="Featured Artist"><Input value={data.featured_artist} onChange={e=>setField("featured_artist", e.target.value)} /></Field>
            <RightsPartySection
              label="Writers"
              partyType="writer"
              parties={data.writer_parties}
              onChange={(next) => setField("writer_parties", next)}
              helperText="Each writer/composer can have multiple PROs and IPI/CAE identifiers. Add one entry per contributing writer."
              emptyHint="No writers added yet. Every track requires at least one writer for compliance."
              addLabel="Add Another Writer"
              testid="track-writer-parties"
            />
            <RightsPartySection
              label="Publishers"
              partyType="publisher"
              parties={data.publisher_parties}
              onChange={(next) => setField("publisher_parties", next)}
              helperText="Publishing companies or self-publishing entities. Each may hold multiple PRO affiliations and IPI/CAE numbers."
              emptyHint="No publishers added yet."
              addLabel="Add Another Publisher"
              testid="track-publisher-parties"
            />
            <RightsPartySection
              label="Publisher Administrators"
              partyType="administrator"
              parties={data.administrator_parties}
              onChange={(next) => setField("administrator_parties", next)}
              helperText="Admin publishers or admin deals covering the work. Optional but useful for the master/publishing chain."
              emptyHint="No administrator added yet."
              addLabel="Add Another Administrator"
              testid="track-administrator-parties"
            />
            <Field label="Master Owner" required><Input value={data.master_owner} onChange={e=>setField("master_owner", e.target.value)} data-testid="track-master-owner-input"/></Field>
            <RightsPartySection
              label="Other Rightsholders"
              partyType="rightsholder"
              parties={data.rightsholder_parties}
              onChange={(next) => setField("rightsholder_parties", next)}
              helperText="Additional rights-holding parties (estates, co-owners, licensors) beyond the master owner. Optional."
              emptyHint="No additional rightsholders added yet."
              addLabel="Add Another Rightsholder"
              testid="track-rightsholder-parties"
            />
            <Field label="Label / Imprint"><Input value={data.label_or_imprint} onChange={e=>setField("label_or_imprint", e.target.value)} /></Field>
            <Field label="Genre" required>
              <SearchableSelect
                value={data.genre}
                onChange={(v) => { setField("genre", v); if (v !== OTHER) setField("genre_other", ""); }}
                options={GENRES}
                placeholder="Select a genre…"
                testid="track-genre-select"
              />
              <OtherField
                triggerValue={data.genre}
                value={data.genre_other}
                onChange={(v) => setField("genre_other", v)}
                label="Custom genre"
                testid="track-genre-other-input"
              />
            </Field>
            <Field label="Sub-Genre">
              <SearchableSelect
                value={data.sub_genre}
                onChange={(v) => { setField("sub_genre", v); if (v !== OTHER) setField("sub_genre_other", ""); }}
                options={SUB_GENRES}
                placeholder="Select a sub-genre…"
                testid="track-sub-genre-select"
              />
              <OtherField
                triggerValue={data.sub_genre}
                value={data.sub_genre_other}
                onChange={(v) => setField("sub_genre_other", v)}
                label="Custom sub-genre"
                testid="track-sub-genre-other-input"
              />
            </Field>
            <Field label="Mood Tags" full><MoodTagsPicker value={data.mood_tags || []} onChange={(arr)=>setField("mood_tags", arr)} /></Field>
            <Field label="BPM"><Input type="number" value={data.bpm ?? ""} onChange={e=>setField("bpm", e.target.value)} /></Field>
            <Field label="Duration" required><DurationField value={data.duration} onChange={(v)=>setField("duration", v)} /></Field>
            <Field label="Explicit / Clean" required>
              <Select value={data.explicit_clean_status || "unset"} onValueChange={v=>setField("explicit_clean_status", v === "unset" ? "" : v)}>
                <SelectTrigger data-testid="track-explicit-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">—</SelectItem>
                  {EXPLICIT_CLEAN.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Best Use">
              <SearchableSelect
                value={data.best_use}
                onChange={(v) => { setField("best_use", v); if (v !== OTHER) setField("best_use_other", ""); }}
                options={BEST_USE_OPTIONS}
                placeholder="Select a sync use…"
                testid="track-best-use-select"
              />
              <OtherField
                triggerValue={data.best_use}
                value={data.best_use_other}
                onChange={(v) => setField("best_use_other", v)}
                label="Custom best use"
                testid="track-best-use-other-input"
              />
            </Field>
            <Field label="Lyrics Available"><Switch checked={data.lyrics_available} onCheckedChange={v=>setField("lyrics_available", v)} /></Field>
            <Field label="Instrumental Available"><Switch checked={data.instrumental_available} onCheckedChange={v=>setField("instrumental_available", v)} /></Field>
            <Field label="Stems Available"><Switch checked={data.stems_available} onCheckedChange={v=>setField("stems_available", v)} /></Field>
            <Field label="Alt Versions Available"><Switch checked={data.alt_versions_available} onCheckedChange={v=>setField("alt_versions_available", v)} /></Field>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="identifiers">
          <Card><CardContent className="p-5 grid md:grid-cols-2 gap-4">
            <Field label="ISRC" required><Input value={data.isrc} onChange={e=>setField("isrc", e.target.value)} className="font-mono" data-testid="track-isrc-input"/></Field>
            <Field label="ISWC"><Input value={data.iswc} onChange={e=>setField("iswc", e.target.value)} className="font-mono" /></Field>
            <Field label="IPI / CAE (legacy — track-level summary)">
              <Input value={data.ipi_cae} onChange={e=>setField("ipi_cae", e.target.value)} className="font-mono" data-testid="track-legacy-ipi-input" />
              <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                Per-party IPI/CAE numbers are now managed on each Writer / Publisher / etc. in the Core tab. This field is preserved for backward compatibility only.
              </div>
              {mode === "edit" && (
                <LegacyRightsCleanup
                  field="ipi"
                  legacyValue={data.ipi_cae}
                  trackId={id}
                  writerParties={data.writer_parties}
                  publisherParties={data.publisher_parties}
                  administratorParties={data.administrator_parties}
                  rightsholderParties={data.rightsholder_parties}
                  onParties={(patch) => setData((d) => ({ ...d, ...patch }))}
                  onCleared={(patch) => setData((d) => ({ ...d, ...patch }))}
                  onSaved={() => refresh(id).catch(() => {})}
                />
              )}
            </Field>
            <Field label="UPC"><Input value={data.upc} onChange={e=>setField("upc", e.target.value)} className="font-mono" /></Field>
            <Field label="Release Date"><Input type="date" value={data.release_date} onChange={e=>setField("release_date", e.target.value)} /></Field>
            <Field label="Copyright Year"><Input value={data.copyright_year} onChange={e=>setField("copyright_year", e.target.value)} /></Field>
            <Field label="LOC Registration #"><Input value={data.loc_registration_number} onChange={e=>setField("loc_registration_number", e.target.value)} className="font-mono" /></Field>
            <Field label="DACR Certificate #"><Input value={data.dacr_certificate_number} onChange={e=>setField("dacr_certificate_number", e.target.value)} className="font-mono" /></Field>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="rights">
          <Card><CardContent className="p-5 grid md:grid-cols-2 gap-4">
            {[
              ["master_status","Master Status", MASTER_STATUS_OPTIONS],
              ["publishing_status","Publishing Status", PUBLISHING_STATUS_OPTIONS],
              ["sync_rights_status","Sync Rights Status", SYNC_RIGHTS_STATUS_OPTIONS],
              ["mechanical_status","Mechanical Status", MECHANICAL_STATUS_OPTIONS],
            ].map(([k,l,opts]) => (
              <Field key={k} label={l}>
                <Select value={data[k] || "unset"} onValueChange={v=>setField(k, v === "unset" ? "" : v)}>
                  <SelectTrigger data-testid={`track-${k}-select`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unset">—</SelectItem>
                    {opts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    {data[k] && !opts.includes(data[k]) && (
                      <SelectItem value={data[k]}>{data[k]} (legacy)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
            ))}
            <Field label="PRO Affiliation (legacy — track-level summary)">
              <PROAffiliationPicker value={data.pro_affiliation} onChange={(v)=>setField("pro_affiliation", v)} />
              <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                Per-party PRO affiliations are now managed on each Writer / Publisher / etc. in the Core tab. This field is preserved for backward compatibility only.
              </div>
              {mode === "edit" && (
                <LegacyRightsCleanup
                  field="pro"
                  legacyValue={data.pro_affiliation}
                  trackId={id}
                  writerParties={data.writer_parties}
                  publisherParties={data.publisher_parties}
                  administratorParties={data.administrator_parties}
                  rightsholderParties={data.rightsholder_parties}
                  onParties={(patch) => setData((d) => ({ ...d, ...patch }))}
                  onCleared={(patch) => setData((d) => ({ ...d, ...patch }))}
                  onSaved={() => refresh(id).catch(() => {})}
                />
              )}
            </Field>
            <div className="md:col-span-2 mt-1">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Music admin / collection</div>
            </div>
            <Field label="Songtrust status">
              <ComplianceDropdown value={data.songtrust_or_admin_status} onChange={(v)=>setField("songtrust_or_admin_status", v)} options={SONGTRUST_STATUS_OPTIONS} testid="track-songtrust-status-select" />
            </Field>
            <Field label="SoundExchange status">
              <ComplianceDropdown value={data.soundexchange_status} onChange={(v)=>setField("soundexchange_status", v)} options={SOUNDEXCHANGE_STATUS_OPTIONS} testid="track-soundexchange-status-select" />
            </Field>
            <Field label="MLC status">
              <ComplianceDropdown value={data.mlc_status} onChange={(v)=>setField("mlc_status", v)} options={MLC_STATUS_OPTIONS} testid="track-mlc-status-select" />
            </Field>
            <Field label="HFA status">
              <ComplianceDropdown value={data.hfa_status} onChange={(v)=>setField("hfa_status", v)} options={HFA_STATUS_OPTIONS} testid="track-hfa-status-select" />
            </Field>
            <Field full label="Mechanical license scope">
              <ComplianceDropdown value={data.mechanical_license_scope} onChange={(v)=>setField("mechanical_license_scope", v)} options={MECHANICAL_LICENSE_SCOPE_OPTIONS} testid="track-mechanical-license-scope-select" />
            </Field>
            <div className="md:col-span-2 rounded-md border bg-secondary/40 p-3 text-[11px] text-muted-foreground leading-relaxed" data-testid="mlc-hfa-boundary-note">
              <span className="font-medium text-foreground">Scope reminder.</span> MLC reflects U.S. digital mechanical royalty collection / admin status. HFA reflects practical mechanical licensing status where applicable. <span className="font-medium text-foreground">Neither MLC nor HFA grants master rights.</span> Master permission must come from the master owner, label, or rights holder. These fields support internal compliance review only — they do not override human approval, clearance gating, or attorney-review requirements.
            </div>
            <Field label="Territory Restrictions" full>
              <SearchableSelect
                value={data.territory_restrictions}
                onChange={(v) => { setField("territory_restrictions", v); if (v !== OTHER) setField("territory_other", ""); }}
                options={TERRITORY_OPTIONS}
                placeholder="Select a territory / market…"
                testid="track-territory-select"
              />
              <OtherField
                triggerValue={data.territory_restrictions}
                value={data.territory_other}
                onChange={(v) => setField("territory_other", v)}
                label="Custom territory description"
                testid="track-territory-other-input"
                asTextarea
              />
            </Field>
            <div className="md:col-span-2 grid sm:grid-cols-2 gap-4 pt-2 border-t mt-2">
              <Field label="Sample / Interpolation Flag"><Switch checked={data.sample_or_interpolation_flag} onCheckedChange={v=>setField("sample_or_interpolation_flag", v)} data-testid="track-sample-flag"/></Field>
              <Field label="Ownership Dispute Flag"><Switch checked={data.ownership_dispute_flag} onCheckedChange={v=>setField("ownership_dispute_flag", v)} data-testid="track-dispute-flag"/></Field>
              <Field label="Estate / Heirship Flag"><Switch checked={data.estate_or_heirship_flag} onCheckedChange={v=>setField("estate_or_heirship_flag", v)} data-testid="track-estate-flag"/></Field>
              <Field label="Third-Party Approval Required"><Switch checked={data.third_party_approval_required} onCheckedChange={v=>setField("third_party_approval_required", v)} data-testid="track-third-party-flag"/></Field>
            </div>
            <Field label="Sample Notes" full><Textarea value={data.sample_notes} onChange={e=>setField("sample_notes", e.target.value)} rows={3} /></Field>
            <Field label="Internal Notes" full><Textarea value={data.internal_notes} onChange={e=>setField("internal_notes", e.target.value)} rows={3} /></Field>
          </CardContent></Card>
        </TabsContent>

        {mode === "edit" && (
        <TabsContent value="documents">
          <Card><CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl">Document Checklist</div>
              <AddDocumentMenu existing={documents} onAdd={addDocument} />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-[140px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No documents tracked yet. Add a document type above.</TableCell></TableRow>
                )}
                {documents.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.document_type}</TableCell>
                    <TableCell>
                      <Select value={d.document_status} onValueChange={(v)=>updateDocStatus(d.id, v)}>
                        <SelectTrigger className="h-9 w-[200px]" data-testid={`doc-status-select-${d.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{DOC_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">{formatNotes(d.notes, { fallback: "\u2014" })}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{d.updated_at?.slice(0,16).replace("T"," ")}</TableCell>
                    <TableCell>
                      <InlineDeleteButton
                        endpoint={`/documents/${d.id}`}
                        recordLabel="Document"
                        recordId={d.id}
                        isSuperAdmin={user?.role === "super_admin"}
                        variant="ghost"
                        onDeleted={async () => { await refresh(id); }}
                        testid={`track-doc-delete-${d.id}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
          {/* Kandake Document Repository (Locked Spec) — vault documents linked
              to this title, with upload adjoining request/status. */}
          <VaultDocumentsPanel trackId={id} />
        </TabsContent>
        )}

        {mode === "edit" && (
        <TabsContent value="compliance">
          <CompliancePanel evaluation={evaluation} track={saved} />
        </TabsContent>
        )}

        {mode === "edit" && (
        <TabsContent value="audio">
          <Card><CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl">Primary Audio</div>
              <Button onClick={()=>audioInputRef.current?.click()} data-testid="track-audio-upload-button" style={{background:"hsl(347 72% 22%)"}}><Upload className="w-4 h-4 mr-2"/>{audioReady ? "Replace audio" : "Upload audio"}</Button>
              <input ref={audioInputRef} type="file" accept="audio/*" className="hidden" onChange={uploadAudio} data-testid="track-audio-file-input"/>
            </div>
            {audioReady ? (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">{saved.audio_original_filename} · {(saved.audio_size||0).toLocaleString()} bytes · {saved.audio_content_type}</div>
                <audio controls src={buildAudioUrl(id)} className="w-full" data-testid="track-audio-player" />
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No audio uploaded yet. Audio is required for any clearance elevation.</div>
            )}
          </CardContent></Card>
        </TabsContent>
        )}

        {mode === "edit" && (
        <TabsContent value="activity">
          <ActivityPanel trackId={id} documentsCount={documents.length} />
        </TabsContent>
        )}

        {mode === "edit" && (
        <TabsContent value="gfmp">
          <PublishToGFMPPanel trackId={id} user={user} onChanged={() => refresh(id).catch(() => {})} />
        </TabsContent>
        )}

        {mode === "edit" && (
        <TabsContent value="ai-provenance">
          <AIProvenancePanel trackId={id} />
        </TabsContent>
        )}
      </Tabs>

      {mode === "edit" && isSuperAdmin && (
        <DeleteRecordButton
          recordType="track"
          recordLabel="Track"
          endpoint={`/tracks/${id}`}
          confirmToken={(saved?.track_title || id || "").slice(0, 16).toUpperCase()}
          confirmTokenLabel="track title"
          isSuperAdmin={true}
          requireTypedConfirmation={true}
          cascadePreview={[
            { label: "Attached documents", count: (documents || []).length },
            { label: "AI Provenance evidence + declarations", count: undefined },
            { label: "Compliance tasks", count: undefined },
          ].filter((r) => (r.count === undefined ? true : r.count > 0))}
          onDeleted={() => navigate("/tracks")}
          testidPrefix="track-delete"
        />
      )}

      <Disclaimer />

      <Dialog open={clearanceOpen} onOpenChange={setClearanceOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set clearance: {clearanceTarget}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will be recorded in the audit log with your reviewer email. Clearance changes are not legal advice.</p>
          <Label className="mt-2">Reviewer notes</Label>
          <Textarea value={clearanceNotes} onChange={e=>setClearanceNotes(e.target.value)} rows={4} data-testid="clearance-notes-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={()=>setClearanceOpen(false)}>Cancel</Button>
            <Button onClick={setClearance} data-testid="clearance-confirm-button" style={{background:"hsl(347 72% 22%)"}}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={attorneyOpen} onOpenChange={setAttorneyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Attorney Review</DialogTitle></DialogHeader>
          {!saved?.attorney_review_required ? (
            <>
              <p className="text-sm text-muted-foreground">Flagging this track for Attorney Review will set its clearance to "Attorney Review Required" and prevent elevation to Client/Corporate-Ready until cleared by qualified counsel.</p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                Attorney Review is an escalation pathway. It does not automatically equate to approval — qualified counsel must review and human compliance approval remains required.
              </p>
              <Label className="mt-3">Reason</Label>
              <SearchableSelect
                value={attorneyReasonCategory}
                onChange={setAttorneyReasonCategory}
                options={ATTORNEY_REVIEW_REASONS}
                placeholder="Select a reason…"
                testid="attorney-reason-select"
              />
              <OtherField
                triggerValue={attorneyReasonCategory}
                value={attorneyReasonDetail}
                onChange={setAttorneyReasonDetail}
                label="Describe the issue"
                placeholder="Provide the specific escalation reason…"
                testid="attorney-reason-other-input"
                asTextarea
              />
              {attorneyReasonCategory && attorneyReasonCategory !== OTHER && (
                <div className="mt-2">
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Optional context</Label>
                  <Textarea
                    value={attorneyReasonDetail}
                    onChange={(e) => setAttorneyReasonDetail(e.target.value)}
                    rows={3}
                    placeholder="Add any supporting context for counsel…"
                    className="mt-1"
                    data-testid="attorney-reason-detail-input"
                  />
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={()=>setAttorneyOpen(false)}>Cancel</Button>
                <Button
                  onClick={()=>flagAttorney(true)}
                  data-testid="attorney-flag-button"
                  disabled={!attorneyReasonCategory || (attorneyReasonCategory === OTHER && !attorneyReasonDetail.trim())}
                  style={{background:"hsl(0 70% 42%)"}}
                >
                  Flag for Attorney Review
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Current reason: <span className="text-foreground">{saved?.attorney_review_reason || "—"}</span></p>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setAttorneyOpen(false)}>Close</Button>
                <Button onClick={()=>flagAttorney(false)} data-testid="attorney-clear-button">Clear Attorney Review flag</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AssetPathwayDialog
        open={pathwayOpen}
        onOpenChange={setPathwayOpen}
        track={saved}
        onChanged={(fresh) => setSaved(fresh)}
      />

      {/* Phase A Run 2 — Withdraw / Archive lifecycle modal. */}
      <ReasonModal
        open={lifecycleModal === "withdraw"}
        onOpenChange={(v) => setLifecycleModal(v ? "withdraw" : null)}
        title="Withdraw Title"
        description="Withdraws this track from new sync, licensing, public catalog, and industry access workflows. Historical records, prior requests, files, notes, and audit history are preserved. Reason is required."
        categoryOptions={WITHDRAW_REASONS}
        confirmLabel="Withdraw Title"
        severity="critical"
        busy={lifecycleBusy}
        testid="withdraw-modal"
        onConfirm={({ category, detail }) => submitLifecycle({
          target: "WITHDRAWN", actionLabel: "Withdrawal", category, detail,
        })}
      />

      <ReasonModal
        open={lifecycleModal === "archive"}
        onOpenChange={(v) => setLifecycleModal(v ? "archive" : null)}
        title="Archive Title"
        description="Archives this track from active surfaces (new sync, licensing, public catalog). Historical records, prior requests, files, notes, and audit history are preserved. Reason is required."
        categoryOptions={ARCHIVE_REASONS}
        confirmLabel="Archive Title"
        severity="high"
        busy={lifecycleBusy}
        testid="archive-modal"
        onConfirm={({ category, detail }) => submitLifecycle({
          target: "ARCHIVED", actionLabel: "Archival", category, detail,
        })}
      />
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs">{label} {required && <span style={{color:"hsl(347 72% 30%)"}}>*</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function CompliancePanel({ evaluation, track }) {
  if (!evaluation) return <div className="text-muted-foreground text-sm">No evaluation yet.</div>;
  const { metadata_missing, missing_documents, missing_for_client_ready, missing_for_corporate_ready, risk_flags, risk_level, next_action, attorney_review_advisory } = evaluation;
  const Section = ({ icon: Icon, title, items, tone }) => (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 mt-1" style={{color: tone}} />
      <div className="flex-1">
        <div className="text-sm font-medium">{title}</div>
        {items?.length ? (
          <ul className="text-sm text-muted-foreground list-disc list-inside">
            {items.map((x,i)=> <li key={i}>{x}</li>)}
          </ul>
        ) : <div className="text-sm text-muted-foreground">None</div>}
      </div>
    </div>
  );
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card><CardContent className="p-5">
        <div className="font-display text-xl mb-2">Readiness Evaluation</div>
        <div className="text-xs text-muted-foreground mb-3">Risk level: <span className="font-medium text-foreground">{risk_level}</span> · Next action: <span className="text-foreground">{next_action}</span></div>
        <div className="divide-y">
          <Section icon={AlertTriangle} title="Missing required metadata" items={metadata_missing} tone="hsl(28 80% 38%)"/>
          <Section icon={ShieldAlert} title="Missing documents (Internally Ready baseline)" items={missing_documents} tone="hsl(28 80% 38%)"/>
          <Section icon={ShieldAlert} title="For Client-Ready, additionally need" items={missing_for_client_ready.filter(x=>!missing_documents.includes(x))} tone="hsl(28 80% 38%)"/>
          <Section icon={ShieldAlert} title="For Corporate-Ready, additionally need" items={missing_for_corporate_ready.filter(x=>!missing_for_client_ready.includes(x))} tone="hsl(28 80% 38%)"/>
          <Section icon={Gavel} title={`Risk flags${attorney_review_advisory ? " — Attorney Review advised" : ""}`} items={risk_flags} tone="hsl(0 70% 42%)"/>
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-5">
        <div className="font-display text-xl mb-2">Eligibility</div>
        <div className="space-y-3">
          <EligibilityRow label="Internally Ready" ok={evaluation.eligible_internally_ready}/>
          <EligibilityRow label="Client-Ready" ok={evaluation.eligible_client_ready}/>
          <EligibilityRow label="Corporate-Ready" ok={evaluation.eligible_corporate_ready}/>
        </div>
        {track?.attorney_review_required && (
          <div className="mt-4 p-3 rounded-md" style={{background:"hsl(0 70% 95%)", color:"hsl(0 70% 30%)"}}>
            <div className="font-medium">Attorney Review Required</div>
            <div className="text-sm">{track?.attorney_review_reason || "Reason not specified"}</div>
          </div>
        )}
        {/* MP5 — Human approval audit display */}
        <div className="mt-4 pt-3 border-t" data-testid="compliance-human-approval">
          <div className="text-xs text-muted-foreground mb-1">Human approval</div>
          {track?.human_approved_by ? (
            <div className="text-sm">
              Approved by <span className="font-mono">{track.human_approved_by}</span> on <span className="font-mono">{(track.human_approval_date || "").slice(0, 19).replace("T", " ")}</span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Pending human review · the system never auto-elevates.</div>
          )}
        </div>
      </CardContent></Card>
    </div>
  );
}

function EligibilityRow({ label, ok }) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="text-sm">{label}</div>
      <div className={`text-xs px-2 py-1 rounded ${ok ? "status-client" : "status-incomplete"}`}>{ok ? "Eligible" : "Not eligible"}</div>
    </div>
  );
}

function AddDocumentMenu({ existing, onAdd }) {
  const used = new Set(existing.map(d => d.document_type));
  return (
    <Select value="" onValueChange={v => onAdd(v)}>
      <SelectTrigger className="w-[260px]" data-testid="track-add-document-select"><SelectValue placeholder="+ Add document type…" /></SelectTrigger>
      <SelectContent>
        {DOC_TYPES.map(t => <SelectItem key={t} value={t} disabled={used.has(t) && t !== "Other"}>{t}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}


function CatalogCodePicker({ value, onChange, onCatalogResolved, isNew }) {
  // MP4 — live registry-backed picker. Lists only ACTIVE catalog codes for
  // selection. Shows the catalogue name as helper text below the selected
  // value. Provides a "Manage Catalog Codes" link for Super Admin.
  const { user } = useAuth();
  const [codes, setCodes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/catalog-codes", { params: { active_only: true } });
        if (!cancelled) setCodes(Array.isArray(r.data) ? r.data : []);
      } catch (err) {
        if (!cancelled) setError(errMsg(err, "Could not load Catalog Codes."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Bubble the full registry record up to the parent when the value
  // resolves to a known active code (used to autofill catalog_name).
  React.useEffect(() => {
    if (!value || !codes.length) return;
    const match = codes.find((c) => c.code === value);
    if (match) onCatalogResolved?.(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, codes]);

  const options = codes.map((c) => ({
    value: c.code,
    label: `[${c.code}] ${c.name}`,
  }));
  const selected = codes.find((c) => c.code === value);
  const valueIsKnown = !!selected;

  return (
    <div>
      <SearchableSelect
        value={value}
        onChange={onChange}
        options={options}
        placeholder={loading ? "Loading Catalog Codes…" : "Select a Catalog Code…"}
        testid="track-catalog-code-select"
        disabled={loading}
      />
      <div className="mt-1 flex items-start justify-between gap-3 flex-wrap">
        <div className="text-[11px] text-muted-foreground min-w-0">
          {error && <span className="text-destructive">{error}</span>}
          {!error && loading && <span>Loading registry…</span>}
          {!error && !loading && value && valueIsKnown && (
            <span data-testid="track-catalog-code-description">
              {selected.name}
              {" · "}
              <span className="text-foreground/70">{selected.catalog_class}</span>
            </span>
          )}
          {!error && !loading && value && !valueIsKnown && (
            <span className="text-amber-700" data-testid="track-catalog-code-legacy">
              Unassigned Legacy Record — this Catalog Code is no longer in the active registry.
            </span>
          )}
          {!error && !loading && !value && isNew && (
            <span className="text-destructive font-medium">
              A Catalog Code is required for new tracks.
            </span>
          )}
        </div>
        {isAdmin?.(user) && (
          <Link
            to="/catalog-codes"
            className="text-[11px] inline-flex items-center gap-1 underline-offset-2 hover:underline whitespace-nowrap"
            style={{ color: "hsl(347 60% 22%)" }}
            data-testid="track-manage-catalog-codes-link"
          >
            Manage Catalog Codes
            <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>
    </div>
  );
}

// TagsField — chip-based multi-tag input that accepts names with spaces,
// commas, periods, ampersands, apostrophes, hyphens. Comma OR Enter commits
// the draft as a chip; Backspace on an empty input removes the last chip.
// This replaces the previous keystroke-splitting CSV input that lost characters.
function TagsField({ value, onChange, placeholder, testid }) {
  const tags = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");

  function commit(raw) {
    const cleaned = (raw || "").trim();
    if (!cleaned) return;
    // Allow the user to paste / type a list with commas — split only at commit.
    const parts = cleaned.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...tags];
    for (const p of parts) {
      if (!next.includes(p)) next.push(p);
    }
    onChange(next);
    setDraft("");
  }

  function handleKey(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && tags.length > 0) {
      e.preventDefault();
      onChange(tags.slice(0, -1));
    }
  }

  return (
    <div data-testid={testid ? `${testid}-wrapper` : undefined}>
      <div className="flex flex-wrap items-center gap-1.5 border border-input rounded-md bg-transparent px-2 py-1.5 min-h-[2.25rem] focus-within:ring-1 focus-within:ring-ring">
        {tags.map((t, i) => (
          <span
            key={`${t}-${i}`}
            className="inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-xs"
            data-testid={testid ? `${testid}-chip-${i}` : undefined}
          >
            {t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
              className="opacity-60 hover:opacity-100"
              data-testid={testid ? `${testid}-chip-remove-${i}` : undefined}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={() => commit(draft)}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm py-1"
          data-testid={testid}
        />
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">
        Press <span className="font-mono">Enter</span> or <span className="font-mono">,</span> to add. Spaces, periods, apostrophes, ampersands, and hyphens all welcome.
      </div>
    </div>
  );
}

function DurationField({ value, onChange }) {
  // Initialize raw text from incoming numeric/string value.
  const initial = React.useMemo(() => {
    if (value == null || value === "") return "";
    if (typeof value === "string") return value;
    // numeric -> MM:SS for friendly editing
    return formatDurationMMSS(value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [raw, setRaw] = useState(initial);

  // Re-sync if the parent re-loads a different track.
  useEffect(() => {
    if (value == null || value === "") {
      setRaw("");
    } else if (typeof value === "number") {
      setRaw(formatDurationMMSS(value));
    } else if (typeof value === "string") {
      // keep user's typed value verbatim
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeof value === "number" ? value : null]);

  const parsed = parseDurationInput(raw);
  const invalid = raw.trim() !== "" && parsed == null;

  function handleChange(e) {
    const v = e.target.value;
    setRaw(v);
    onChange(v);
  }
  function handleBlur() {
    if (raw.trim() === "") { onChange(null); return; }
    const p = parseDurationInput(raw);
    if (p != null) {
      // normalize input to MM:SS canonical text and propagate the parsed seconds value
      const mmss = formatDurationMMSS(p);
      setRaw(mmss);
      onChange(p);
    }
  }
  return (
    <div>
      <Input
        value={raw}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="MM:SS (e.g. 3:42) or seconds (e.g. 222)"
        data-testid="track-duration-input"
        aria-invalid={invalid ? "true" : "false"}
        className={invalid ? "border-[hsl(0_70%_42%)] focus-visible:ring-[hsl(0_70%_42%)]" : ""}
      />
      <div className="text-[11px] text-muted-foreground mt-1" data-testid="track-duration-preview">
        {raw.trim() === "" ? (
          <span>Stored internally as seconds · displayed as MM:SS.</span>
        ) : invalid ? (
          <span className="text-[hsl(0_70%_42%)]">Invalid duration. Use MM:SS (e.g. 3:42) or raw seconds (e.g. 222).</span>
        ) : (
          <span>= <span className="font-mono">{formatDurationMMSS(parsed)}</span> ({Math.round(parsed)} s)</span>
        )}
      </div>
    </div>
  );
}

function ComplianceDropdown({ value, onChange, options, testid }) {
  const v = value || "unset";
  return (
    <Select value={v} onValueChange={(nv) => onChange(nv === "unset" ? "" : nv)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="unset">—</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        {value && !options.includes(value) && (
          <SelectItem value={value}>{value} (legacy)</SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

function PROAffiliationPicker({ value, onChange }) {
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/pro-directory").then(r => {
      if (!active) return;
      setSocieties(r.data || []);
    }).catch(() => {
      if (active) setSocieties([]);
    }).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  // Build the canonical display string we store: "CODE — Name (Country)"
  const activeOptions = societies.filter(s => s.active_status !== false);

  function chosenLabel(s) {
    const country = s.country_or_territory ? ` (${s.country_or_territory})` : "";
    return `${s.society_code} — ${s.society_name}${country}`;
  }

  // If current value isn't in options, render it as a (legacy) item so we don't lose data.
  const valueExists = !value || activeOptions.some(s => chosenLabel(s) === value);

  return (
    <div>
      <Select value={value || "unset"} onValueChange={(v) => onChange(v === "unset" ? "" : v)}>
        <SelectTrigger data-testid="track-pro-affiliation-select">
          <SelectValue placeholder={loading ? "Loading…" : "Select a PRO / CMO…"} />
        </SelectTrigger>
        <SelectContent className="max-h-[320px]">
          <SelectItem value="unset">— None —</SelectItem>
          {activeOptions.map((s) => {
            const label = chosenLabel(s);
            return <SelectItem key={s.pro_id || s.id || label} value={label}>{label}</SelectItem>;
          })}
          {value && !valueExists && <SelectItem value={value}>{value} (legacy / free text)</SelectItem>}
        </SelectContent>
      </Select>
      <div className="text-[11px] text-muted-foreground mt-1">
        Backed by the <a href="/pro-directory" className="underline">PRO / CMO Reference Directory</a>. Super Admin can add or update entries.
      </div>
    </div>
  );
}

function MoodTagsPicker({ value, onChange }) {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null);   // MP11 Phase 2 — index of custom chip being renamed
  const [editDraft, setEditDraft] = useState("");
  const selected = Array.isArray(value) ? value : [];

  function toggle(tag) {
    const lower = tag.toLowerCase().trim();
    if (!lower) return;
    if (selected.includes(lower)) {
      onChange(selected.filter((t) => t !== lower));
    } else {
      onChange([...selected, lower]);
    }
  }

  function addDraft(e) {
    e.preventDefault();
    if (!draft.trim()) return;
    toggle(draft);
    setDraft("");
  }

  // MP11 Phase 2 — rename a CUSTOM (user-added) mood tag in place without
  // deleting the parent track. Suggested (preset) tags are immutable.
  function commitRename(i) {
    const nextLower = editDraft.toLowerCase().trim();
    if (!nextLower) {
      // Empty rename = remove the chip cleanly.
      onChange(selected.filter((_, idx) => idx !== i));
      setEditing(null);
      setEditDraft("");
      return;
    }
    if (selected.includes(nextLower) && selected[i] !== nextLower) {
      // Would collide with an existing chip — just remove the old one to
      // dedupe rather than creating a stealth duplicate.
      onChange(selected.filter((_, idx) => idx !== i));
    } else {
      const next = selected.slice();
      next[i] = nextLower;
      onChange(next);
    }
    setEditing(null);
    setEditDraft("");
  }

  // Show union of suggestions + custom tags already on the track (so legacy
  // values render as chips alongside the MP4-suggested list).
  const suggestionSet = new Set(MOOD_TAGS);
  const extraCustom = selected.filter((t) => !suggestionSet.has(t));
  const allShown = [...MOOD_TAGS, ...extraCustom];

  return (
    <div data-testid="mood-tags-picker">
      <div className="flex flex-wrap gap-1.5">
        {allShown.map((t) => {
          const on = selected.includes(t);
          const isCustom = !suggestionSet.has(t);
          const idxInSelected = selected.indexOf(t);
          // Rename-in-place UI for CUSTOM chips only.
          if (isCustom && editing === idxInSelected) {
            return (
              <span
                key={`edit-${t}-${idxInSelected}`}
                className="inline-flex items-center gap-1 rounded border border-[hsl(347_72%_22%)]/40 px-2 py-0.5"
                data-testid={`mood-chip-edit-${t}`}
              >
                <Input
                  autoFocus
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitRename(idxInSelected); }
                    if (e.key === "Escape") { setEditing(null); setEditDraft(""); }
                  }}
                  onBlur={() => commitRename(idxInSelected)}
                  className="h-6 text-[11px] w-[140px] py-0"
                  data-testid={`mood-chip-edit-input-${t}`}
                />
              </span>
            );
          }
          return (
            <button
              key={t}
              type="button"
              onClick={() => toggle(t)}
              onDoubleClick={() => {
                if (isCustom && on) {
                  setEditing(idxInSelected);
                  setEditDraft(t);
                }
              }}
              title={isCustom && on ? "Double-click to rename" : undefined}
              data-testid={`mood-chip-${t}`}
              className={`text-[11px] px-2 py-0.5 rounded border transition ${
                on
                  ? "bg-[hsl(347_72%_22%)] text-white border-[hsl(347_72%_22%)]"
                  : "bg-secondary text-foreground/80 border-transparent hover:border-[hsl(347_72%_22%)]/40"
              }`}
            >
              {t}{on ? <X className="w-3 h-3 inline ml-1 -mt-0.5" /> : null}
            </button>
          );
        })}
      </div>
      <form onSubmit={addDraft} className="mt-2 flex gap-2 items-center">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add custom mood…"
          className="h-8 max-w-[220px]"
          data-testid="mood-add-input"
        />
        <Button type="submit" variant="outline" size="sm" data-testid="mood-add-button">Add</Button>
      </form>
      <div className="text-[10px] text-muted-foreground mt-1.5">
        Click a chip to add/remove. Double-click a selected <span className="italic">custom</span> mood to rename it.
      </div>
    </div>
  );
}

function ActivityPanel({ trackId, documentsCount }) {
  const [audit, setAudit] = useState([]);
  const [subs, setSubs] = useState([]);
  const [aiNotes, setAiNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [a, s, ai] = await Promise.all([
          api.get(`/audit?record_type=track&record_id=${trackId}&limit=100`),
          api.get(`/submissions?selected_track_id=${trackId}`),
          api.get(`/ai-review-notes?track_id=${trackId}`),
        ]);
        setAudit(a.data || []);
        setSubs(s.data || []);
        setAiNotes(ai.data || []);
      } catch (e) {
        // soft fail in detail tab
      } finally {
        setLoading(false);
      }
    }
    if (trackId) load();
  }, [trackId]);

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card data-testid="activity-ai-review-notes">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: "hsl(40 48% 32%)" }} />
            <div className="font-display text-xl">AI Review Notes</div>
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary text-muted-foreground ml-auto">AI-Lite · Rule-assisted</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Deterministic compliance notes generated from this track's fields. No LLM is invoked. Manage from the <a href="/ai-review-notes" className="underline">AI Review Notes</a> module.
          </p>
          <Button
            size="sm" variant="outline" className="mt-2"
            onClick={() => window.location.assign(`/ai-review-notes?generate=${trackId}`)}
            data-testid="activity-generate-ai-note"
          >
            <Sparkles className="w-4 h-4 mr-2" />Generate AI Review Note
          </Button>
          {loading ? null : aiNotes.length === 0 ? (
            <div className="text-xs text-muted-foreground mt-3">No AI review notes saved for this track yet.</div>
          ) : (
            <ul className="text-xs mt-3 space-y-2">
              {aiNotes.map((n) => (
                <li key={n.ai_note_id} className="border rounded p-2" data-testid={`track-ai-note-${n.ai_note_id}`}>
                  <div className="font-mono text-[10px] text-muted-foreground">{(n.generated_at || "").slice(0, 19).replace("T", " ")} · {n.generated_by}</div>
                  <div className="text-sm mt-1">{n.compliance_summary}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{n.suggested_next_action}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card data-testid="activity-linked-documents">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <FolderOpen className="w-4 h-4" style={{ color: "hsl(347 72% 22%)" }} />
            <div className="font-display text-xl">Linked Documents</div>
            <span className="text-xs text-muted-foreground ml-auto">{documentsCount} on file</span>
          </div>
          <p className="text-xs text-muted-foreground">Open the Documents tab to view and update each document's status.</p>
        </CardContent>
      </Card>

      <Card data-testid="activity-linked-submissions">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Send className="w-4 h-4" style={{ color: "hsl(347 72% 22%)" }} />
            <div className="font-display text-xl">Linked Submissions</div>
            <span className="text-xs text-muted-foreground ml-auto">{subs.length}</span>
          </div>
          {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : subs.length === 0 ? (
            <div className="text-xs text-muted-foreground">No submission drafts linked to this track yet.</div>
          ) : (
            <ul className="divide-y">
              {subs.map((s) => (
                <li key={s.id} className="py-2 text-sm">
                  <div className="flex justify-between gap-2">
                    <a className="font-mono text-xs hover:underline" href={`/submissions/${s.id}`} data-testid={`linked-submission-${s.id}`}>{s.submission_id}</a>
                    <span className="text-[11px] text-muted-foreground">{s.submission_status}</span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{s.project_title || "—"}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card data-testid="activity-audit-history">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-4 h-4" style={{ color: "hsl(347 72% 22%)" }} />
            <div className="font-display text-xl">Audit History</div>
            <span className="text-xs text-muted-foreground ml-auto">{audit.length}</span>
          </div>
          {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : audit.length === 0 ? (
            <div className="text-xs text-muted-foreground">No audit entries recorded for this track yet.</div>
          ) : (
            <ul className="divide-y max-h-[360px] overflow-auto">
              {audit.map((a) => (
                <li key={a.audit_id} className="py-2">
                  <div className="text-sm"><span className="font-medium">{a.action_type}</span></div>
                  <div className="text-[11px] text-muted-foreground font-mono">{(a.timestamp || "").slice(0, 19).replace("T", " ")} · {a.user_email || a.user_name || "system"}</div>
                  {hasVisibleNotes(a.notes) ? <div className="text-xs text-muted-foreground mt-1">{formatNotes(a.notes, { fallback: "\u2014" })}</div> : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// MP10 — Industry Beta Access (super_admin only)
//
// Dedicated control card for opening a track to the Industry Beta surface.
// Persists via the audit-logged endpoint PUT /api/tracks/{id}/beta-flags,
// NOT via the generic track Save. Both `beta_visible` AND
// `industry_review_approved` must be true on the server for the track to
// surface in /api/beta/catalog.
// ---------------------------------------------------------------------------
function BetaAccessCard({ trackId, betaVisible, industryReviewApproved, onSaved }) {
  const [visible, setVisible] = useState(!!betaVisible);
  const [approved, setApproved] = useState(!!industryReviewApproved);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Resync local state if the parent refreshes the track (e.g., after save).
  useEffect(() => { setVisible(!!betaVisible); }, [betaVisible]);
  useEffect(() => { setApproved(!!industryReviewApproved); }, [industryReviewApproved]);

  const dirty = visible !== !!betaVisible || approved !== !!industryReviewApproved;
  const live = visible && approved;

  async function saveBetaAccess() {
    setSaving(true);
    try {
      await api.put(`/tracks/${trackId}/beta-flags`, {
        beta_visible: visible,
        industry_review_approved: approved,
        note: (note || "").trim() || undefined,
      });
      toast.success("Beta access updated");
      setNote("");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      className="mb-4 border-[hsl(347_60%_22%)]/30"
      data-testid="track-beta-access-card"
    >
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" style={{ color: "hsl(347 60% 22%)" }} />
              <div className="font-display text-base" data-testid="track-beta-access-title">
                Industry Beta Access
              </div>
              <span
                className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded"
                style={{
                  background: "hsl(40 48% 92%)",
                  color: "hsl(347 60% 22%)",
                  border: "1px solid hsl(40 48% 78%)",
                }}
                data-testid="track-beta-access-tier-badge"
              >
                Super Admin only
              </span>
              <span
                className={`ml-auto text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded ${
                  live ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"
                }`}
                data-testid="track-beta-access-live-badge"
              >
                {live ? "Live in beta catalog" : "Not in beta catalog"}
              </span>
            </div>
            <p className="text-[11.5px] text-muted-foreground mt-1 leading-relaxed">
              Controls whether this track appears in the restricted{" "}
              <span className="font-mono">/beta</span> portal for invited Industry Beta Reviewers.{" "}
              <span className="font-medium text-foreground">Both toggles must be ON</span> for the
              track to surface. Each change is recorded in the audit log
              (<span className="font-mono">track.beta_flags_changed</span>). This is a controlled
              approval action — not an ordinary catalog edit.
            </p>
          </div>
        </div>

        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div className="flex items-center justify-between border rounded-md p-3">
            <div className="pr-2">
              <Label htmlFor="beta-visible-switch" className="text-[12.5px] font-medium">
                Beta-visible
              </Label>
              <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                Admin opens this track to the beta surface at all.
              </div>
            </div>
            <Switch
              id="beta-visible-switch"
              checked={visible}
              onCheckedChange={setVisible}
              disabled={saving}
              data-testid="track-beta-visible-switch"
            />
          </div>
          <div className="flex items-center justify-between border rounded-md p-3">
            <div className="pr-2">
              <Label htmlFor="industry-review-approved-switch" className="text-[12.5px] font-medium">
                Industry-review approved
              </Label>
              <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                Explicit content-clearance for invited reviewers (separate from internal clearance).
              </div>
            </div>
            <Switch
              id="industry-review-approved-switch"
              checked={approved}
              onCheckedChange={setApproved}
              disabled={saving}
              data-testid="track-industry-review-approved-switch"
            />
          </div>
        </div>

        <div className="mt-3 grid md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Approval note (optional · audited)
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Why is this work being opened (or closed) to industry beta reviewers?"
              className="mt-1 text-[12.5px]"
              disabled={saving}
              data-testid="track-beta-access-note"
            />
          </div>
          <Button
            onClick={saveBetaAccess}
            disabled={!dirty || saving}
            data-testid="track-beta-access-save"
            style={{ background: "hsl(347 72% 22%)" }}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save Beta Access"}
          </Button>
        </div>

        <div className="mt-2 text-[11px] text-muted-foreground">
          Beta access is independent of clearance status. Toggling these flags neither approves a
          clearance nor licenses the work — beta reviewers receive evaluation-only access.
        </div>
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// MP11 Phase 2 — Legacy Track-Level PRO / IPI Cleanup workflow.
//
// Renders inline underneath a legacy field. The operator can either
//   (1) Attach the legacy value to a chosen structured rights party as a
//       new PRO or IPI/CAE entry, then clear the legacy summary; OR
//   (2) Clear the legacy summary without transferring (with a required
//       reason typed into the audit trail).
//
// Path (1) is a two-step atomic flow:
//   a. Update local state so the party gets a new sub-entry (state is
//      persisted by the NEXT save via PUT /tracks/{id} — the operator sees
//      "Save Track" is now needed).
//   b. Simultaneously call POST /tracks/{id}/legacy-rights/clear which
//      persists the cleared legacy field AND writes a discrete audit event
//      ``track.legacy_rights.cleared`` with the target party recorded.
//
// The consequential clear-without-transfer action requires a typed reason.
// ---------------------------------------------------------------------------
function LegacyRightsCleanup({
  field, legacyValue, trackId,
  writerParties, publisherParties, administratorParties, rightsholderParties,
  onParties, onCleared, onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [targetParty, setTargetParty] = useState("");
  const [clearOnAttach, setClearOnAttach] = useState(true);
  const [reason, setReason] = useState("");
  const [confirmClearOnly, setConfirmClearOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasValue = !!(legacyValue || "").toString().trim();
  const partyList = useMemo(() => {
    const rows = [];
    for (const [key, arr, label] of [
      ["writer_parties", writerParties, "Writer"],
      ["publisher_parties", publisherParties, "Publisher"],
      ["administrator_parties", administratorParties, "Administrator"],
      ["rightsholder_parties", rightsholderParties, "Rightsholder"],
    ]) {
      for (const p of (arr || [])) {
        rows.push({
          value: `${key}::${p.id}`,
          label: `${p.name || "(unnamed)"} — ${label}`,
          key, party: p, category: label,
        });
      }
    }
    return rows;
  }, [writerParties, publisherParties, administratorParties, rightsholderParties]);

  if (!hasValue) return null;

  async function performAttach() {
    if (!targetParty || busy) return;
    setBusy(true);
    try {
      const found = partyList.find((r) => r.value === targetParty);
      if (!found) throw new Error("Party not found in track state.");
      const key = found.key;
      const parties = ({
        writer_parties: writerParties,
        publisher_parties: publisherParties,
        administrator_parties: administratorParties,
        rightsholder_parties: rightsholderParties,
      })[key] || [];
      const newSubId = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : String(Date.now()) + "-" + Math.random().toString(16).slice(2);
      const nextParties = parties.map((p) => {
        if (p.id !== found.party.id) return p;
        if (field === "pro") {
          return {
            ...p,
            pros: [
              ...(p.pros || []),
              {
                id: newSubId,
                pro_name: (legacyValue || "").toString().trim(),
                society_code: "",
                territory: "",
                membership_ref: "",
                notes: "Migrated from legacy track-level PRO summary.",
              },
            ],
          };
        }
        return {
          ...p,
          ipis: [
            ...(p.ipis || []),
            {
              id: newSubId,
              ipi_cae_number: (legacyValue || "").toString().trim(),
              associated_pro: "",
              capacity: "",
              notes: "Migrated from legacy track-level IPI/CAE summary.",
            },
          ],
        };
      });
      // Push updated party array into the parent form state so the operator
      // can see the transfer BEFORE hitting Save Track. Save Track will
      // persist the parties. In parallel we call the dedicated legacy-rights
      // endpoint to clear the legacy field with a distinct audit event.
      onParties({ [key]: nextParties });
      if (clearOnAttach) {
        await api.post(`/tracks/${trackId}/legacy-rights/clear`, {
          clear_pro: field === "pro",
          clear_ipi: field === "ipi",
          attached_to_party_id: found.party.id,
          attached_to_party_type: found.party.party_type || (found.category || "").toLowerCase(),
          reason: (reason || "").trim() ||
            `Legacy ${field.toUpperCase()} attached to ${found.party.name || "unnamed party"} (${found.category}).`,
        });
        if (field === "pro") onCleared({ pro_affiliation: "" });
        else onCleared({ ipi_cae: "", ipi_cae_numbers: "" });
      }
      toast.success(
        `Attached to ${found.party.name || "party"}` +
        (clearOnAttach ? " and cleared legacy summary. Save the track to persist the new PRO/IPI entry." : ".")
      );
      setOpen(false);
      setTargetParty("");
      setReason("");
      if (clearOnAttach) onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not attach + clear");
    } finally {
      setBusy(false);
    }
  }

  async function performClearOnly() {
    if (busy || !reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tracks/${trackId}/legacy-rights/clear`, {
        clear_pro: field === "pro",
        clear_ipi: field === "ipi",
        attached_to_party_id: "",
        attached_to_party_type: "",
        reason: reason.trim(),
      });
      if (field === "pro") onCleared({ pro_affiliation: "" });
      else onCleared({ ipi_cae: "", ipi_cae_numbers: "" });
      toast.success("Legacy summary cleared.");
      setConfirmClearOnly(false);
      setOpen(false);
      setReason("");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not clear legacy field");
    } finally {
      setBusy(false);
    }
  }

  const fieldLabel = field === "pro" ? "PRO" : "IPI/CAE";

  return (
    <div
      className="mt-2 rounded-md border border-amber-500/25 bg-amber-500/[0.06] px-2.5 py-2"
      data-testid={`legacy-cleanup-${field}`}
    >
      <div className="text-[11px] text-amber-900/80">
        A legacy track-level {fieldLabel} value is present. Attach it to a specific rights party or, if it is not attributable, clear it deliberately with a reason.
      </div>
      <div className="mt-1.5">
        {!open ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            data-testid={`legacy-cleanup-${field}-open`}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Resolve legacy {fieldLabel}…
          </Button>
        ) : (
          <div className="space-y-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Attach to rights party
              </Label>
              <Select value={targetParty} onValueChange={setTargetParty}>
                <SelectTrigger
                  className="mt-1 h-8 text-[12.5px]"
                  data-testid={`legacy-cleanup-${field}-target`}
                >
                  <SelectValue placeholder={partyList.length ? "Choose a party…" : "No parties yet — add a Writer/Publisher first"} />
                </SelectTrigger>
                <SelectContent>
                  {partyList.map((row) => (
                    <SelectItem key={row.value} value={row.value}>{row.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {partyList.length === 0 && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  Add at least one Writer / Publisher / Administrator / Rightsholder in the Core tab first.
                </div>
              )}
            </div>
            <label className="flex items-center gap-1.5 text-[11.5px]">
              <input
                type="checkbox"
                checked={clearOnAttach}
                onChange={(e) => setClearOnAttach(e.target.checked)}
                data-testid={`legacy-cleanup-${field}-clear-toggle`}
              />
              Clear the legacy track-level {fieldLabel} summary after attaching.
            </label>
            <div>
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Reason / note (optional · captured in audit)
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder={`e.g. Verified with archived split sheet — ${fieldLabel} belongs to Jane Doe.`}
                className="mt-1 text-[12px]"
                data-testid={`legacy-cleanup-${field}-reason`}
              />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                onClick={performAttach}
                disabled={!targetParty || busy}
                style={{ background: "hsl(347 72% 22%)" }}
                data-testid={`legacy-cleanup-${field}-attach`}
              >
                {busy ? "Working…" : (clearOnAttach ? "Attach & clear" : "Attach (keep legacy)")}
              </Button>
              {!confirmClearOnly ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmClearOnly(true)}
                  data-testid={`legacy-cleanup-${field}-clear-only`}
                >
                  Clear without transfer…
                </Button>
              ) : (
                <div
                  className="flex items-center gap-1.5 rounded border border-amber-500/30 px-2 py-1"
                  data-testid={`legacy-cleanup-${field}-clear-confirm`}
                >
                  <span className="text-[11px] text-amber-900/80">
                    A reason is required to clear without transfer.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmClearOnly(false)}
                    data-testid={`legacy-cleanup-${field}-clear-cancel`}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={performClearOnly}
                    disabled={!reason.trim() || busy}
                    style={{ background: "hsl(0 70% 42%)", color: "white" }}
                    data-testid={`legacy-cleanup-${field}-clear-confirm-yes`}
                  >
                    Clear now
                  </Button>
                </div>
              )}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => { setOpen(false); setTargetParty(""); setReason(""); setConfirmClearOnly(false); }}
                data-testid={`legacy-cleanup-${field}-cancel`}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
